/**
 * API DATETIME values are stored in UTC.
 * Naive strings (no Z/offset) are parsed as UTC via Date.UTC —
 * never `new Date(isoString)` (iOS PWAs ignore Z).
 *
 * Display is always Europe/Berlin wall-clock, never device TZ.
 */

export const BERLIN_TZ = 'Europe/Berlin';

const DATETIME_PARTS = /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2})(?:\.(\d+))?)?)?(Z|[+-]\d{2}:?\d{2})?$/i;

export const parseMysqlUtcDatetime = (value) => {
  if (value == null || value === '') return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  const raw = String(value).trim();
  if (!raw) return null;

  const match = raw.match(DATETIME_PARTS);
  if (!match) return null;

  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const hasTime = match[4] != null;
  const hour = Number(match[4] ?? 12);
  const minute = Number(match[5] ?? 0);
  const second = Number(match[6] ?? 0);
  const ms = match[7] ? Number(String(match[7]).padEnd(3, '0').slice(0, 3)) : 0;
  const tz = match[8];

  if (!hasTime && !tz) {
    const date = new Date(Date.UTC(year, month - 1, day, 12, 0, 0));
    return Number.isNaN(date.getTime()) ? null : date;
  }

  let utcMs = Date.UTC(year, month - 1, day, hour, minute, second, ms);
  if (tz && tz.toUpperCase() !== 'Z') {
    const sign = tz[0] === '-' ? -1 : 1;
    const digits = tz.slice(1).replace(':', '');
    const offH = Number(digits.slice(0, 2));
    const offM = Number(digits.slice(2, 4) || '0');
    utcMs -= sign * (offH * 60 + offM) * 60 * 1000;
  }

  const date = new Date(utcMs);
  return Number.isNaN(date.getTime()) ? null : date;
};

export const isMysqlUtcAfterNow = (value) => {
  const parsed = parseMysqlUtcDatetime(value);
  return Boolean(parsed && parsed.getTime() > Date.now());
};

const lastUtcSundayOfMonth = (year, monthIndex) => {
  const date = new Date(Date.UTC(year, monthIndex + 1, 0));
  date.setUTCDate(date.getUTCDate() - date.getUTCDay());
  return date;
};

export const berlinOffsetMinutesAt = (date) => {
  const year = date.getUTCFullYear();
  const dstStart = lastUtcSundayOfMonth(year, 2);
  dstStart.setUTCHours(1, 0, 0, 0);
  const dstEnd = lastUtcSundayOfMonth(year, 9);
  dstEnd.setUTCHours(1, 0, 0, 0);
  const t = date.getTime();
  return t >= dstStart.getTime() && t < dstEnd.getTime() ? 120 : 60;
};

export const berlinWallClockParts = (date) => {
  const local = new Date(date.getTime() + berlinOffsetMinutesAt(date) * 60 * 1000);
  const pad = (n) => String(n).padStart(2, '0');
  return {
    day: pad(local.getUTCDate()),
    month: pad(local.getUTCMonth() + 1),
    year: String(local.getUTCFullYear()),
    hour: pad(local.getUTCHours()),
    minute: pad(local.getUTCMinutes()),
  };
};

export const toBerlinDateInputValue = (date = new Date()) => {
  const p = berlinWallClockParts(date);
  return `${p.year}-${p.month}-${p.day}`;
};

export const toBerlinTimeInputValue = (date = new Date()) => {
  const p = berlinWallClockParts(date);
  return `${p.hour}:${p.minute}`;
};

/** Berlin wall-clock date+time inputs → UTC ISO. */
export const combineBerlinDateTimeToUtcIso = (dateInput, timeInput) => {
  if (!dateInput || !timeInput) return null;
  const [year, month, day] = String(dateInput).split('-').map(Number);
  const [hour, minute] = String(timeInput).split(':').map(Number);
  if (![year, month, day, hour, minute].every(Number.isFinite)) return null;

  const asUtcMs = Date.UTC(year, month - 1, day, hour, minute, 0);
  let offset = berlinOffsetMinutesAt(new Date(asUtcMs));
  let utcMs = asUtcMs - offset * 60 * 1000;
  offset = berlinOffsetMinutesAt(new Date(utcMs));
  utcMs = asUtcMs - offset * 60 * 1000;
  const parsed = new Date(utcMs);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
};

/** Berlin wall-clock, e.g. `20.08.2026, 05:00`. */
export const formatMysqlUtcDatetimeBerlinDe = (value) => {
  const parsed = parseMysqlUtcDatetime(value);
  if (!parsed) return '–';
  const p = berlinWallClockParts(parsed);
  return `${p.day}.${p.month}.${p.year}, ${p.hour}:${p.minute}`;
};

export const formatMysqlUtcDatetimeDe = (value) => formatMysqlUtcDatetimeBerlinDe(value);

export const formatMysqlUtcDateDe = (value) => {
  const parsed = parseMysqlUtcDatetime(value);
  if (!parsed) return '';
  const p = berlinWallClockParts(parsed);
  return `${p.day}.${p.month}.${p.year}`;
};

export const formatMysqlUtcDateShortDe = (value) => {
  const parsed = parseMysqlUtcDatetime(value);
  if (!parsed) return '';
  const p = berlinWallClockParts(parsed);
  return `${p.day}.${p.month}.`;
};

export const formatMysqlUtcTimeDe = (value) => {
  const parsed = parseMysqlUtcDatetime(value);
  if (!parsed) return '';
  const p = berlinWallClockParts(parsed);
  return `${p.hour}:${p.minute}`;
};

const berlinWallClockToInstantFromParts = (year, month, day, hour, minute) => {
  const roughUtc = Date.UTC(Number(year), Number(month) - 1, Number(day), hour, minute, 0);
  const offsetMin = berlinOffsetMinutesAt(new Date(roughUtc));
  return new Date(
    Date.UTC(Number(year), Number(month) - 1, Number(day), hour, minute, 0) - offsetMin * 60 * 1000,
  );
};

const berlinWallClockMatches = (instant, year, month, day, hour, minute) => {
  const p = berlinWallClockParts(instant);
  return Number(p.year) === Number(year)
    && Number(p.month) === Number(month)
    && Number(p.day) === Number(day)
    && Number(p.hour) === hour
    && Number(p.minute) === minute;
};

const isBerlinSpringForwardDay = (year, month, day) => {
  if (Number(month) !== 3) return false;
  const lastSunUtc = lastUtcSundayOfMonth(Number(year), 2);
  const p = berlinWallClockParts(lastSunUtc);
  return Number(p.year) === Number(year)
    && Number(p.month) === Number(month)
    && Number(p.day) === Number(day);
};

/** Berlin wall-clock → UTC instant. Skips spring-forward gap; prefers later hour on autumn overlap. */
export const berlinWallClockToInstant = (year, month, day, hour, minute) => {
  const y = Number(year);
  const mo = Number(month);
  const d = Number(day);
  const h = Number(hour);
  const min = Number(minute);

  if (isBerlinSpringForwardDay(y, mo, d) && h === 2) {
    return berlinWallClockToInstantFromParts(y, mo, d, 3, min);
  }

  const candidate = berlinWallClockToInstantFromParts(y, mo, d, h, min);
  if (berlinWallClockMatches(candidate, y, mo, d, h, min)) {
    return candidate;
  }

  const laterCandidate = new Date(candidate.getTime() + 60 * 60 * 1000);
  if (berlinWallClockMatches(laterCandidate, y, mo, d, h, min)) {
    return laterCandidate;
  }

  return candidate;
};

export const roundBerlinInstantTo15Minutes = (instant) => {
  const p = berlinWallClockParts(instant);
  const totalMinutes = Number(p.hour) * 60 + Number(p.minute);
  const remainder = totalMinutes % 15;
  if (remainder === 0) {
    return new Date(instant.getTime());
  }
  return new Date(instant.getTime() + (15 - remainder) * 60 * 1000);
};

/** Schedule UI datetimes without timezone suffix are Berlin wall-clock. */
export const parseBerlinWallClockToUtcDate = (value) => {
  if (value == null || value === '') return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  const raw = String(value).trim();
  if (!raw) return null;
  if (/Z$/i.test(raw) || /[+-]\d{2}:?\d{2}$/.test(raw)) {
    return parseMysqlUtcDatetime(raw);
  }
  const normalized = raw.replace('T', ' ').slice(0, 16);
  const match = normalized.match(/^(\d{4})-(\d{2})-(\d{2}) (\d{2}):(\d{2})/);
  if (!match) return null;
  return berlinWallClockToInstant(
    match[1],
    match[2],
    match[3],
    Number(match[4]),
    Number(match[5]),
  );
};
